/**
 * features/auth/hooks/useAuth.ts
 *
 * CAMBIO PRINCIPAL:
 * loadCajeroFromSession ahora devuelve { ok, cajero, error, detail }
 * en lugar de ActiveCajero | null.
 *
 * Si la query falla por RLS u otro error → ok=false, error visible.
 * El fallback silencioso con storeId='' ya NO existe cuando hay sesión válida.
 *
 * Los callers (login, rehydration, ShiftGate) reciben el error y lo muestran.
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase } from '@lib/supabase/client'
import { servidorAlcanzable, marcarServidorAlcanzable } from '@lib/net/reachability'
import { useAuthContext } from '@app/providers/AuthProvider'
import type { User } from '@supabase/supabase-js'
import type { Role } from '@shared/types/roles.types'
import {
  getCompanyConfig,
  resolveEffectiveStoreConfig,
} from '@features/organization/utils/companyConfig'
import { getStoreConfig } from '@features/organization/utils/storeConfig'
import {
  resolveAccessScope,
  buildLegacyAccessibleStore,
  buildLegacyAccessibleBusiness,
} from '@features/organization/utils/accessControl'
import type {
  AccessScope,
  AccessibleBusiness,
  AccessibleStore,
} from '@features/organization/utils/accessControl'

// ── Tipos ──────────────────────────────────────────────────────────────────

/** Alias de Role — fuente de verdad única en roles.types.ts */
export type UserRole = Role

export interface ActiveCajero {
  // ── Identidad ──────────────────────────────────────────────────────────
  id: string
  name: string
  email: string
  role: UserRole
  /** SUPER USUARIO (users.is_owner). Alcance global: ve/opera/administra todo. */
  isOwner: boolean
  // ── Tienda (legacy + primaria) ──────────────────────────────────────────
  storeId: string // users.store_id — campo legacy, siempre presente
  storeCode: string
  storeName: string | null
  storeConfig: Record<string, unknown>
  // ── Empresa ────────────────────────────────────────────────────────────
  businessId: string | null
  businessBrand: string | null
  businessName: string | null
  // ── Acceso corporativo (E1.3) ───────────────────────────────────────────
  accessScope: AccessScope
  accessibleBusinesses: AccessibleBusiness[]
  accessibleStores: AccessibleStore[]
  primaryBusinessId: string | null
  primaryStoreId: string | null
  extraPermissions: string[] // permisos adicionales al rol base
  // ── Contexto activo (E1.4) — puede cambiar via setActiveContext ─────────
  activeBusinessId: string | null
  activeBusinessBrand: string | null
  activeBusinessName: string | null
  activeStoreId: string
  activeStoreCode: string
  activeStoreName: string | null
}

export type LoadCajeroResult =
  | { ok: true; cajero: ActiveCajero }
  | { ok: false; error: string; detail: string }

/**
 * Devuelve true si el usuario tiene acceso corporativo/administrativo y NO
 * debe ser bloqueado por la lógica de turnos de caja.
 *
 * Criterios (OR):
 *   - accessScope === 'global'  → director con acceso multi-empresa
 *   - role 'admin' o 'director' → control completo de empresa/red
 *   - extraPermissions incluye 'ver_dashboard_corporativo' o 'administrar_empresas'
 *
 * Los roles operativos (cajero, encargado, supervisor, vendedor, técnico…)
 * retornan false y siguen sujetos a la regla de un turno abierto a la vez.
 */
export function isSupervisorMode(cajero: ActiveCajero | null): boolean {
  if (!cajero) return false
  if (cajero.accessScope === 'global') return true
  if (cajero.role === 'admin' || cajero.role === 'director') return true
  const perms = cajero.extraPermissions ?? []
  return perms.includes('ver_dashboard_corporativo') || perms.includes('administrar_empresas')
}

// ── loadCajeroFromSession — sin fallback silencioso ────────────────────────

/**
 * Carga el perfil del cajero desde public.users.
 * Devuelve { ok: false, error, detail } si algo falla — NUNCA fallback silencioso.
 *
 * Orden de intentos:
 *   1. WHERE auth_user_id = session.user.id  (camino normal)
 *   2. WHERE email = session.user.email       (dev: seed sin auth_user_id)
 *
 * Si AMBOS fallan con error real → devuelve { ok:false, error: mensaje exacto }.
 * Si AMBOS devuelven 0 filas (no error) → el usuario no existe en public.users.
 */
export async function loadCajeroFromSession(
  authUser: User,
  setCajero: (c: ActiveCajero) => void
): Promise<LoadCajeroResult> {
  // Sin servidor el perfil no se puede releer, y uno a medias es peor que ninguno: dejaba
  // al usuario como "acceso corporativo, sin tiendas asignadas" justo cuando más falta le
  // hace operar. El contexto ya autorizado que quedó guardado NO se pisa.
  // No amplía permisos: es el mismo alcance que concedió el servidor, y toda escritura se
  // sigue validando contra el servidor al sincronizar.
  const guardado = () => useCajeroStore.getState().cajero
  if (!servidorAlcanzable() && guardado()) {
    return { ok: true, cajero: guardado()! }
  }
  // Paso 0: Forzar aplicación de la sesión al cliente antes de hacer queries.
  // Sin esto, el primer REST call puede salir con Authorization: Bearer <anon_key>
  // (race condition entre inicialización del cliente y restauración desde localStorage).
  const {
    data: { session: fresh },
  } = await supabase.auth.getSession()

  const authUserId = fresh?.user?.id ?? authUser.id
  const email = fresh?.user?.email ?? authUser.email ?? ''

  // ── Intento 1: WHERE auth_user_id = uuid ─────────────────────────────────
  const { data: byId, error: errById } = await supabase
    .from('users')
    .select('id, name, role, store_id')
    .eq('auth_user_id', authUserId)
    .eq('active', true)
    .maybeSingle()

  if (errById) {
    if (errById.code === 'PGRST301') {
      // JWT rechazado por PostgREST — causas comunes en local:
      //   • VITE_SUPABASE_ANON_KEY en .env.local no coincide con `supabase status`
      //   • Sesión de localStorage de una instancia Supabase anterior
      // No es un error de código. Limpiar sesión y pedir re-login.
      console.warn('[useAuth] ⚠ PGRST301 — JWT inválido para PostgREST. Limpiando sesión...')
      console.warn('[useAuth] Verificar VITE_SUPABASE_ANON_KEY en .env.local vs `supabase status`')
      void supabase.auth.signOut()
      return {
        ok: false,
        error: 'Sesión inválida. Por favor inicia sesión de nuevo.',
        detail: `PGRST301: ${errById.message}`,
      }
    }
    // Sin código de PostgREST no hubo respuesta del API: es una caída del servidor, no un
    // perfil inválido. Se marca inalcanzable y se conserva el contexto guardado.
    if (!errById.code) {
      marcarServidorAlcanzable(false)
      if (guardado()) return { ok: true, cajero: guardado()! }
      return {
        detail: errById.message,
        ok: false,
        error: 'Sin conexión con el servidor y sin contexto guardado en este equipo.',
      }
    }
    const msg = `Query users falló: ${errById.message} (code: ${errById.code})`
    console.error('[useAuth] ❌', msg)
    return { ok: false, error: msg, detail: errById.message }
  }

  if (byId) {
    return _buildCajero(byId, email, setCajero)
  }

  // ── Intento 2: vincular vía RPC SECURITY DEFINER ──────────────────────────
  // NO se hace SELECT por email desde el cliente: esa consulta corre como
  // `authenticated` y queda sujeta a las policies de RLS de `users`, que a
  // su vez dependen de que el usuario YA esté vinculado — un círculo
  // imposible de romper desde aquí. link_my_auth_user() lo rompe del lado
  // del servidor (SECURITY DEFINER) sin exponer ninguna búsqueda genérica
  // por email: solo puede vincular el auth.uid() de quien la invoca.
  const { data: linkResult, error: linkRpcError } = await supabase.rpc('link_my_auth_user')

  if (linkRpcError) {
    const msg = `RPC link_my_auth_user falló: ${linkRpcError.message}`
    console.error('[useAuth] ❌', msg)
    return { ok: false, error: msg, detail: linkRpcError.message }
  }

  const linked = linkResult as {
    ok: boolean
    error?: string
    profile?: { id: string; name: string; role: string; store_id: string }
  }

  if (linked.ok && linked.profile) {
    return _buildCajero(linked.profile, email, setCajero)
  }

  // ── No se pudo vincular ────────────────────────────────────────────────
  // El mensaje ya viene claro desde la RPC (no existe perfil, inactivo,
  // eliminado, o el email ya pertenece a otra cuenta de acceso).
  const noProfileMsg = [
    `Usuario autenticado pero sin perfil vinculable en public.users.`,
    `auth.user.id: ${authUserId}`,
    `auth.user.email: ${email}`,
    `Motivo: ${linked.error ?? 'desconocido'}`,
  ].join('\n')

  console.error('[useAuth] ❌ Sin perfil:\n' + noProfileMsg)
  return {
    ok: false,
    error: linked.error ?? 'Sin perfil en el sistema.',
    detail: noProfileMsg,
  }
}

// ── Helper interno ─────────────────────────────────────────────────────────

async function _buildCajero(
  row: { id: string; name: string; role: string; store_id: string },
  email: string,
  setCajero: (c: ActiveCajero) => void
): Promise<LoadCajeroResult> {
  // ── Round trip 1: tienda + acceso corporativo en paralelo ────────────────
  const [storeResult, bizAccessResult, storeAccessResult, ownerResult] = await Promise.all([
    row.store_id
      ? supabase
          .from('stores')
          .select('code, brand, name, business_id, config')
          .eq('id', row.store_id)
          .maybeSingle()
      : Promise.resolve({ data: null, error: null }),

    supabase
      .from('user_business_access')
      .select('id, business_id, role, permissions, is_primary')
      .eq('user_id', row.id)
      .eq('active', true),

    supabase
      .from('user_store_access')
      .select('id, store_id, business_id, role, permissions, is_primary')
      .eq('user_id', row.id)
      .eq('active', true),

    // is_owner (SUPER USUARIO): self-select RLS-safe (users_self_select).
    // Fuente única del flag, independiente de la ruta de entrada a _buildCajero.
    supabase.from('users').select('is_owner').eq('id', row.id).maybeSingle(),
  ])

  type StoreRow = {
    code: string
    brand: string
    name: string
    business_id: string | null
    config: Record<string, unknown>
  }
  const storeRow = storeResult.data as StoreRow | null

  const storeCode = storeRow?.code ?? ''
  const storeName: string | null = storeRow?.name ?? null
  const storeBrand: string | null = storeRow?.brand ?? null
  let storeConfig: Record<string, unknown> = (storeRow?.config as Record<string, unknown>) ?? {}
  const businessId: string | null = storeRow?.business_id ?? null
  let businessBrand: string | null = storeBrand
  let businessName: string | null = null

  if (storeResult.error) {
    console.warn('[useAuth] No se pudo cargar la tienda:', storeResult.error.message)
  }

  // ── Round trip 2: empresa (serial — depende de business_id de la tienda) ──
  if (businessId) {
    const { data: bizRaw, error: bizErr } = await supabase
      .from('businesses')
      .select('id, brand, name, config')
      .eq('id', businessId)
      .maybeSingle()

    if (bizErr) {
      console.warn('[useAuth] No se pudo cargar empresa (puede no existir aún):', bizErr.message)
    } else if (bizRaw) {
      const biz = bizRaw as {
        id: string
        brand: string
        name: string
        config: Record<string, unknown>
      }
      businessBrand = biz.brand
      businessName = biz.name

      const companyConf = getCompanyConfig((biz.config as Record<string, unknown>) ?? {})
      const storeConf = getStoreConfig(storeConfig)
      storeConfig = resolveEffectiveStoreConfig(companyConf, storeConf) as unknown as Record<
        string,
        unknown
      >
    }
  }

  // ── Contexto de acceso corporativo ────────────────────────────────────────
  type BizAccessRow = {
    id: string
    business_id: string
    role: string
    permissions: string[]
    is_primary: boolean
  }
  type StoreAccessRow = {
    id: string
    store_id: string
    business_id: string | null
    role: string | null
    permissions: string[]
    is_primary: boolean
  }

  const bizAccessRows = (bizAccessResult.data ?? []) as BizAccessRow[]
  const storeAccessRows = (storeAccessResult.data ?? []) as StoreAccessRow[]

  if (bizAccessResult.error) {
    console.warn(
      '[useAuth] user_business_access no disponible (puede no existir aún):',
      bizAccessResult.error.message
    )
  }
  if (storeAccessResult.error) {
    console.warn(
      '[useAuth] user_store_access no disponible (puede no existir aún):',
      storeAccessResult.error.message
    )
  }

  // Empresas accesibles — desde user_business_access, fallback a empresa de la tienda
  let accessibleBusinesses: AccessibleBusiness[] = bizAccessRows.map(r => ({
    businessId: r.business_id,
    role: r.role,
    isPrimary: r.is_primary,
  }))
  if (accessibleBusinesses.length === 0 && businessId) {
    accessibleBusinesses = [buildLegacyAccessibleBusiness(businessId, row.role)]
  }

  // Tiendas accesibles — desde user_store_access, fallback a tienda legacy
  let accessibleStores: AccessibleStore[] = storeAccessRows.map(r => ({
    storeId: r.store_id,
    storeCode: r.is_primary ? storeCode : '',
    businessId: r.business_id ?? null,
    role: r.role ?? row.role,
    isPrimary: r.is_primary,
  }))
  if (accessibleStores.length === 0 && row.store_id) {
    accessibleStores = [buildLegacyAccessibleStore(row.store_id, storeCode, businessId, row.role)]
  }

  // Permisos adicionales acumulados de los access rows
  const extraPermissions = Array.from(
    new Set([
      ...bizAccessRows.flatMap(r => r.permissions ?? []),
      ...storeAccessRows.flatMap(r => r.permissions ?? []),
    ])
  )

  const primaryStoreId =
    (storeAccessRows.find(r => r.is_primary) ?? storeAccessRows[0])?.store_id ??
    row.store_id ??
    null
  const primaryBusinessId =
    (bizAccessRows.find(r => r.is_primary) ?? bizAccessRows[0])?.business_id ?? businessId ?? null

  const isOwner = (ownerResult.data as { is_owner: boolean } | null)?.is_owner === true
  const accessScope = resolveAccessScope(row.role as UserRole, accessibleBusinesses, isOwner)

  // Guardar acceso en Dexie para modo offline
  const { db } = await import('@lib/dexie/schema')
  const syncedAt = Date.now()
  if (bizAccessRows.length > 0) {
    db.userBusinessAccess
      .bulkPut(
        bizAccessRows.map(r => ({
          id: r.id,
          user_id: row.id,
          business_id: r.business_id,
          role: r.role,
          permissions: r.permissions ?? [],
          is_primary: r.is_primary,
          active: true,
          updated_at: new Date().toISOString(),
          synced_at: syncedAt,
        }))
      )
      .catch(e => console.warn('[useAuth] Dexie userBusinessAccess:', e))
  }
  if (storeAccessRows.length > 0) {
    db.userStoreAccess
      .bulkPut(
        storeAccessRows.map(r => ({
          id: r.id,
          user_id: row.id,
          store_id: r.store_id,
          business_id: r.business_id ?? null,
          role: r.role ?? null,
          permissions: r.permissions ?? [],
          is_primary: r.is_primary,
          active: true,
          updated_at: new Date().toISOString(),
          synced_at: syncedAt,
        }))
      )
      .catch(e => console.warn('[useAuth] Dexie userStoreAccess:', e))
  }

  // Contexto activo inicial: primera tienda primaria disponible o fallback legacy
  const activeStoreEntry =
    accessibleStores.find(s => s.isPrimary) ??
    accessibleStores.find(s => s.storeId === (primaryStoreId ?? row.store_id)) ??
    accessibleStores[0]

  const cajero: ActiveCajero = {
    id: row.id,
    name: row.name,
    email,
    role: row.role as UserRole,
    isOwner,
    storeId: row.store_id,
    storeCode,
    storeName,
    storeConfig,
    businessId,
    businessBrand,
    businessName,
    accessScope,
    accessibleBusinesses,
    accessibleStores,
    primaryBusinessId,
    primaryStoreId,
    extraPermissions,
    // E1.4 — contexto activo (cambiable sin re-login)
    activeBusinessId: primaryBusinessId ?? businessId ?? null,
    activeBusinessBrand: businessBrand ?? null,
    activeBusinessName: businessName ?? null,
    activeStoreId: activeStoreEntry?.storeId ?? primaryStoreId ?? row.store_id ?? '',
    activeStoreCode:
      activeStoreEntry?.storeId === row.store_id
        ? storeCode
        : activeStoreEntry?.storeCode || storeCode,
    activeStoreName: storeName ?? null,
  }

  setCajero(cajero)
  return { ok: true, cajero }
}

// ── Store Zustand ──────────────────────────────────────────────────────────

interface ActiveContextUpdate {
  businessId: string | null
  businessBrand: string | null
  businessName: string | null
  storeId: string
  storeCode: string
  storeName: string | null
}

interface CajeroStore {
  cajero: ActiveCajero | null
  profileError: string | null // error persistido para mostrar en ShiftGate
  setCajero: (c: ActiveCajero) => void
  setProfileError: (e: string | null) => void
  clearCajero: () => void
  setActiveContext: (ctx: ActiveContextUpdate) => void
}

export const useCajeroStore = create<CajeroStore>()(
  persist(
    set => ({
      cajero: null,
      profileError: null,
      setCajero: cajero => set({ cajero, profileError: null }),
      setProfileError: profileError =>
        set(s => ({
          profileError,
          // Si hay error, limpiar cajero stale — punto 4 de Etapa 5
          cajero: profileError != null ? null : s.cajero,
        })),
      clearCajero: () => set({ cajero: null, profileError: null }),
      setActiveContext: ctx =>
        set(s => {
          if (!s.cajero) return { cajero: null }

          // Aislamiento de catálogo: si cambia empresa o tienda, purgar el
          // catálogo operativo cacheado (products/stock) ANTES de que el POS
          // re-sincronice el contexto nuevo. Evita fuga de productos entre
          // empresas y purga fantasmas Dexie. Solo cache local; NO toca Supabase.
          // El fetch de POS tiene 300ms de debounce → el clear termina antes.
          const contextChanged =
            s.cajero.activeBusinessId !== ctx.businessId || s.cajero.activeStoreId !== ctx.storeId
          if (contextChanged) {
            void import('@lib/dexie/schema').then(({ clearCatalogCache }) =>
              clearCatalogCache().catch(() => {})
            )
          }

          return {
            cajero: {
              ...s.cajero,
              activeBusinessId: ctx.businessId,
              activeBusinessBrand: ctx.businessBrand,
              activeBusinessName: ctx.businessName,
              activeStoreId: ctx.storeId,
              activeStoreCode: ctx.storeCode,
              activeStoreName: ctx.storeName,
            },
          }
        }),
    }),
    {
      name: 'celmaxx-cajero-v4', // v4: fuerza recarga tras fix de accesos director/multitienda
      // profileError no se persiste — se recalcula en cada sesión
      partialize: s => ({ cajero: s.cajero }),
    }
  )
)

// ── Hook principal ─────────────────────────────────────────────────────────

export function useAuth() {
  const { session, loading: authLoading, signOut } = useAuthContext()
  const { cajero, setCajero, setProfileError, clearCajero, setActiveContext } = useCajeroStore()
  const [pinLoading, setPinLoading] = useState(false)
  const [pinError, setPinError] = useState<string | null>(null)
  // Ref para prevenir llamadas concurrentes a loadCajeroFromSession
  const profileLoadingRef = useRef(false)

  /**
   * Rehydration al montar.
   * Recarga si cajero es null O si storeId está vacío (stale del fallback viejo).
   */
  useEffect(() => {
    if (authLoading) return
    if (!session?.user) return
    if (cajero?.id && cajero?.storeId) {
      // Director/admin sin empresas accesibles = cache stale tras cambio de DB
      const isLikelyStale =
        (cajero.role === 'director' || cajero.role === 'admin') &&
        (cajero.accessibleBusinesses?.length ?? 0) === 0
      if (!isLikelyStale) return
    }
    if (profileLoadingRef.current) return // ya hay una carga en progreso

    profileLoadingRef.current = true

    loadCajeroFromSession(session.user, setCajero)
      .then(result => {
        if (!result.ok) setProfileError(result.error)
      })
      .catch(e => console.error('[useAuth] loadCajeroFromSession error:', e))
      .finally(() => {
        profileLoadingRef.current = false
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authLoading,
    session?.user,
    cajero?.id,
    cajero?.storeId,
    cajero?.role,
    cajero?.accessibleBusinesses?.length,
    setCajero,
    setProfileError,
  ])

  /**
   * Login: solo lanza error si las credenciales son incorrectas.
   * Si el perfil de public.users falla, se guarda en profileError y
   * ShiftGate lo muestra — pero el usuario SÍ entra al sistema.
   */
  const login = useCallback(
    async (email: string, password: string) => {
      const {
        data: { session: s },
        error: authError,
      } = await supabase.auth.signInWithPassword({ email, password })

      if (authError) throw authError
      if (!s?.user) throw new Error('No se pudo iniciar sesión.')

      // Cargar perfil con fallbacks — si falla, ShiftGate lo muestra
      const result = await loadCajeroFromSession(s.user, setCajero)
      if (!result.ok) {
        // Guardar el error para mostrarlo en ShiftGate, pero NO bloquear el login
        setProfileError(result.error)
        console.warn('[useAuth] Perfil no cargado, mostrando error en ShiftGate:', result.error)
      }
      // El login fue exitoso — el navegador irá al POS y ShiftGate manejará el resto
    },
    [setCajero, setProfileError]
  )

  const logout = useCallback(async () => {
    // Limpiar el marcador de "tienda confirmada" para que el próximo login
    // vuelva a preguntar Empresa/Tienda (no heredar la selección anterior).
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i)
        if (k?.startsWith('celmaxx-store-confirmed:')) sessionStorage.removeItem(k)
      }
    } catch {
      // sessionStorage no disponible — ignorar.
    }
    clearCajero()
    await signOut()
  }, [signOut, clearCajero])

  const validatePin = useCallback(
    async (userId: string, pin: string, action: string): Promise<boolean> => {
      setPinLoading(true)
      setPinError(null)
      try {
        const {
          data: { session: s },
        } = await supabase.auth.getSession()
        if (!s) throw new Error('Sin sesión activa')
        const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/validar_pin`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${s.access_token}`,
          },
          body: JSON.stringify({ userId, pin, action }),
        })
        const body = (await res.json()) as {
          data?: { valid: boolean }
          error?: { message: string }
        }
        if (!res.ok || body.error) {
          setPinError(body.error?.message ?? 'PIN incorrecto')
          return false
        }
        return body.data?.valid === true
      } catch {
        setPinError('Error de conexión')
        return false
      } finally {
        setPinLoading(false)
      }
    },
    []
  )

  return {
    isAuthenticated: !!session,
    isLoading: authLoading,
    cajero,
    login,
    logout,
    validatePin,
    pinLoading,
    pinError,
    clearPinError: () => setPinError(null),
    setActiveContext,
  }
}
